import { secureApi } from './secureApi';

/**
 * Owner Monthly Report API — monthly owner performance reports.
 *
 * Backend routes: be/routes/ownerReportRoutes.js (mounted at /api/v1/owner-reports)
 *
 * All endpoints are JWT-authenticated, so this file uses `secureApi` only — the
 * shared instance whose interceptor attaches `x-auth-token` and runs the silent
 * 401 refresh-retry. (`/finance` itself still calls raw axios with manual Bearer
 * headers; that is pre-existing debt tracked in tasks/legacy-axios-cleanup.md and
 * deliberately not touched here.)
 *
 * Role scoping is enforced SERVER-side: an owner receives only their own released
 * reports, as an allow-list of fields. Nothing here re-implements that boundary.
 *
 * `preserveBackendError` on the mutations: secureApi rewrites 4xx bodies to
 * generic Arabic messages by default, which would hide the specific reason an ops
 * action was refused ("the month has not closed", "a failed report cannot be
 * released"). Reads keep the default rewrite — there is nothing specific to say.
 */

const ownerReportApi = {

  // ==========================================
  // READ — owner + ops
  // ==========================================

  /**
   * Report list, newest month first. Metadata only, no payload.
   * @param {Object} [params] - { owner, periodMonth } — `owner` is ops-only
   */
  getReports: async (params = {}) => {
    try {
      const response = await secureApi.get('/api/v1/owner-reports', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching owner reports:', error);
      throw error;
    }
  },

  /** One report with its full payload. */
  getReport: async (reportId) => {
    try {
      const response = await secureApi.get(`/api/v1/owner-reports/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching owner report:', error);
      throw error;
    }
  },

  // ==========================================
  // OPS ACTIONS
  // ==========================================

  /** Make a report visible to its owner. Idempotent. */
  releaseReport: async (reportId) => {
    try {
      const response = await secureApi.post(
        `/api/v1/owner-reports/${reportId}/release`,
        {},
        { preserveBackendError: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error releasing owner report:', error);
      throw error;
    }
  },

  /**
   * Rebuild one owner + month. Clears `released` — the caller must warn first.
   *
   * ⚠️ Needs its own timeout. The builder runs three analytics aggregations per
   * property, sequentially, so a multi-property owner easily exceeds secureApi's
   * shared 10s default. A timeout there is the worst possible outcome: the server
   * finishes the rebuild and clears `released`, while the operator is told it
   * failed. 60s matches the precedent in `cleaningChecklistApi.js`.
   *
   * @param {string} owner - owner user id
   * @param {string} periodMonth - 'YYYY-MM' business month (must be closed)
   */
  regenerateReport: async (owner, periodMonth) => {
    try {
      const response = await secureApi.post(
        '/api/v1/owner-reports/regenerate',
        { owner, periodMonth },
        { preserveBackendError: true, timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      console.error('Error regenerating owner report:', error);
      throw error;
    }
  }
};

export default ownerReportApi;
export { ownerReportApi };
